/**
 * The booking steps' rules (shared-client-screens D4 "Checkout"), one home for the Mini App's
 * booking pages and the studio website's checkout, booked and event pages: which payment
 * methods the payment step shows, the review's price row and approval note, the When lines,
 * the end screen's rows, the booking photo header, the event page's button and the hold line.
 * Pure; each surface draws them and wires its own navigation.
 */
import type { SessionService } from '@/api/types';
import type { BookingHeroProps } from '@/components/booking/BookingHero';
import { formatCurrency } from '@/components/payment/formatCurrency';
import { formatWhen } from '@/pages/SessionDetailPage/detailFormat';
import type { BookingDetail } from '@/types/bookingDetail';
import { formatDuration } from '@/utils/formatDateTime';
import { buildGoogleMapsUrl } from '@/utils/mapLinks';
import { phoneCallLine, phoneToCall, virtualPlatformLabel, type PhoneCall } from '@telify/shared/browser';

type Offering = Pick<SessionService, 'paymentRequirement' | 'price'> & Partial<Pick<SessionService, 'depositAmount'>>;

// ── Payment step ─────────────────────────────────────────────────────────────

/** The payment step's surface, from the server's availability flags only. */
export type PaymentView = 'depositBlocked' | 'hardBlock' | 'bothMethods' | 'cardOnly' | 'venmoOnly' | 'cardUnavailableDegrade';

/**
 * Which methods the payment step shows (rows P2, P3). A deposit is blocked (deposit-balance-flow
 * has not shipped); an unknown availability fails closed. The website offers no Venmo (row P1):
 * it passes `venmo: false`, so a card that is not available there is the hard block.
 */
export function paymentView(
  service: Partial<Pick<SessionService, 'paymentRequirement' | 'acceptedPaymentMethods' | 'cardAvailable' | 'venmoAvailable'>> | null | undefined,
  options: { venmo?: boolean } = {},
): PaymentView {
  const cardUsable = service?.cardAvailable === true;
  const venmoUsable = options.venmo !== false && service?.venmoAvailable === true;
  if (service?.paymentRequirement === 'DEPOSIT') return 'depositBlocked';
  if (cardUsable && venmoUsable) return 'bothMethods';
  if (cardUsable) return 'cardOnly';
  if (venmoUsable) return (service?.acceptedPaymentMethods ?? []).includes('CARD') ? 'cardUnavailableDegrade' : 'venmoOnly';
  return 'hardBlock';
}

/** A payment step notice, drawn as the app's banner. */
export interface PaymentNotice {
  readonly variant: 'info' | 'warning';
  readonly title: string;
  readonly body: string;
}

/**
 * The payment step's notices in the app's words (rows P2 to P5), for the app's payment page, the
 * website's phone payment screen and its computer payment step alike. `booked` is Pay now: the
 * booking already exists, so card payments paused keep it rather than book nothing.
 */
export function paymentNotice(
  kind: 'depositBlocked' | 'hardBlock' | 'cardUnavailableDegrade' | 'windowTooClose' | 'benignTerminal',
  businessName: string,
  { booked = false }: { booked?: boolean } = {},
): PaymentNotice {
  switch (kind) {
    case 'depositBlocked':
      return {
        variant: 'info',
        title: "Deposits aren't available in this checkout yet",
        body: "Please reach out to the business to arrange your deposit. Your booking wasn't charged.",
      };
    case 'hardBlock':
      if (booked) {
        return {
          variant: 'warning',
          title: 'Card payments are paused right now',
          body: `${businessName}'s card processing is temporarily unavailable. Nothing was charged and your booking is kept. Please try again later, or reach out to ${businessName}.`,
        };
      }
      return {
        variant: 'warning',
        title: 'Card payments are paused right now',
        body: `This booking can only be paid by card, and ${businessName}'s card processing is temporarily unavailable. Nothing was booked. Please try again later, or reach out to ${businessName}.`,
      };
    case 'cardUnavailableDegrade':
      return {
        variant: 'info',
        title: 'Card payment is paused right now',
        body: "This business's card processing is temporarily unavailable. You can still secure your booking with Venmo.",
      };
    case 'windowTooClose':
      // Row P4: the app's words without the em dash.
      return { variant: 'warning', title: 'Your held time slot expired', body: 'Not enough time left to pay. Go back and pick a new time. Nothing was charged.' };
    case 'benignTerminal':
      return { variant: 'info', title: "You're all set", body: 'Your earlier payment went through. Check your messages for your confirmation.' };
  }
}

/** A declined card (B9): the card's own words, and the way out; "or use another payment method" only beside one (row P6). */
export function declineNotice(title: string, otherMethods: boolean): PaymentNotice {
  return {
    variant: 'info',
    title,
    body: `No charge was made. Try a different card${otherMethods ? ', or use another payment method' : ''}. Your spot is still held.`,
  };
}

// ── Review ───────────────────────────────────────────────────────────────────

export interface PriceRow {
  label: string;
  value: string;
  /** The amount on the right, or null. */
  amount: string | null;
}

const isFree = (service: Offering | null | undefined) => service?.paymentRequirement === 'NONE' || !service?.price;

/**
 * The review's price row (rows V1, V2, V3; web-sign-in-checkout D10): a free booking "Price ·
 * Free", a free request "Nothing to pay · Free", "Pay at the session" with its amount, a request
 * paid online "Collected after approval", else "Due at checkout".
 */
export function reviewPriceRow(service: Offering | null | undefined, request: boolean): PriceRow {
  const price = service?.price ?? 0;
  if (isFree(service)) return request ? { label: 'Price', value: 'Nothing to pay', amount: 'Free' } : { label: 'Price', value: 'Free', amount: null };
  if (service?.paymentRequirement === 'PAY_AT_SESSION') return { label: 'Price', value: 'Pay at the session', amount: formatCurrency(price) };
  if (request) return { label: 'Payment', value: 'Collected after approval', amount: null };
  // Online payment (a defensive fallback: the flow skips review for it and goes to payment).
  const online = service?.paymentRequirement === 'DEPOSIT' ? (service.depositAmount ?? price) : price;
  return { label: 'Price', value: 'Due at checkout', amount: formatCurrency(online) };
}

/** The review's note on a request (row V4): when payment is collected, if at all. */
export function approvalNote(service: Offering | null | undefined): string {
  if (isFree(service)) return 'This booking requires approval.';
  if (service?.paymentRequirement === 'PAY_AT_SESSION') return 'This booking requires approval. If approved, payment is collected at your session.';
  return 'This booking requires approval. Payment will be collected after approval.';
}

/** The request-sent line (web-sign-in-checkout D10, row K7). */
export function requestSentLine(studioName: string, payAtSession: boolean): string {
  return `${studioName || 'The studio'} will confirm shortly. We'll message you the moment it's approved.${payAtSession ? ' If approved, payment is collected at your session.' : ''}`;
}

/** The When rows: "Tue, Jun 17 · 6:30 PM", or every date of a series in its own zone ("Tue, Jun 17, 6:30 PM"). */
export function whenLines(startsAt: string | null | undefined, tz: string, series?: { dates: readonly string[]; timezone: string | null } | null): string[] {
  if (series && series.dates.length > 0) {
    const fmt = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: series.timezone ?? tz,
    });
    return series.dates.map((d) => fmt.format(new Date(d)));
  }
  return startsAt ? [formatWhen(startsAt, tz)] : [];
}

/** The last step's call line (service-by-phone D5): its words, the number on file, and whether the step asks for one. */
export interface PhoneCallStep {
  /** "Dana Wilcher will call you at +1 555 010 0199."; null while there is no number. */
  readonly line: string | null;
  /** The number on file, trimmed; null when there is none. */
  readonly phone: string | null;
  /** No number on file: the step asks for one and the bottom button waits. */
  readonly needsNumber: boolean;
}

/** Why the step waits with no number on file (the server refuses with the same words). */
export const PHONE_NEEDED = 'Add the number to call to book this service.';

/** The call line's step for a by-phone service; null for any other. */
export function phoneCallStep(phoneCall: PhoneCall | null | undefined): PhoneCallStep | null {
  if (!phoneCall) return null;
  const phone = phoneToCall(phoneCall.phone);
  return { line: phone ? phoneCallLine(phoneCall.caller, phone) : null, phone, needsNumber: phone === null };
}

/** The hold line under the step's title (row R9): an event holds a spot, a session a time slot. */
export function reservedLine(kind: 'service' | 'event', until: string): string {
  return `${kind === 'event' ? 'Spot' : 'Time slot'} reserved until ${until}`;
}

// ── The booking photo header ─────────────────────────────────────────────────

export interface BookingHeroInput {
  photo: { url: string | null; focalX?: number | null; focalY?: number | null; scale?: number | null } | null;
  name: string | null | undefined;
  provider: { name: string; photoUrl: string | null } | null;
  startsAt: string | null | undefined;
  tz: string;
  /** A request's review and request-sent views; the form and payment steps read "Your booking" (row R6). */
  request: boolean;
}

/** The booking steps' photo header (rows R6, R7, R8): the service's zoom, no provider line without one. */
export function bookingHeroProps({ photo, name, provider, startsAt, tz, request }: BookingHeroInput): BookingHeroProps {
  return {
    imageUrl: photo?.url ?? null,
    focalPoint: { x: photo?.focalX ?? null, y: photo?.focalY ?? null, scale: photo?.scale ?? null },
    name: name || 'Your booking',
    providerName: provider?.name ?? '',
    providerPhoto: provider?.photoUrl ?? null,
    whenLabel: startsAt ? formatWhen(startsAt, tz) : null,
    eyebrow: request ? 'Your request' : 'Your booking',
  };
}

/** A booking's photo header from the booking itself (the end screen, Pay now). */
export function bookingDetailHeroProps(b: BookingDetail, tz: string, request: boolean): BookingHeroProps {
  return bookingHeroProps({
    photo: { url: b.service.imageUrl ?? null, focalX: b.service.focalPointX, focalY: b.service.focalPointY, scale: b.service.focalPointScale },
    name: b.service.name,
    provider: b.provider ? { name: b.provider.name, photoUrl: b.provider.avatarUrl } : null,
    startsAt: b.scheduledAt,
    tz,
    request,
  });
}

// ── The event page ───────────────────────────────────────────────────────────

export interface EventCta {
  kind: 'view' | 'soldOut' | 'closed' | 'reserve';
  label: string;
  /** The line under the button: the event's time while it can be reserved. */
  sub: string | undefined;
  disabled: boolean;
}

/** The event page's button, in the app's order: View booking, Sold out, Registration closed, Reserve your spot. */
export function eventCta(e: { booked: boolean; spotsRemaining: number; isBookable: boolean; reserving: boolean; whenText: string }): EventCta {
  if (e.booked) return { kind: 'view', label: 'View booking', sub: undefined, disabled: false };
  if (e.spotsRemaining <= 0) return { kind: 'soldOut', label: 'Sold out', sub: undefined, disabled: true };
  // The frozen cutoff passed, or the final minute before start.
  if (!e.isBookable) return { kind: 'closed', label: 'Registration closed', sub: undefined, disabled: true };
  return { kind: 'reserve', label: e.reserving ? 'Reserving…' : 'Reserve your spot', sub: e.whenText, disabled: e.reserving };
}

/** An event's length from its own start and end (row E5). */
export function eventMinutes(startTime: string, endTime: string): number {
  return Math.round((new Date(endTime).getTime() - new Date(startTime).getTime()) / 60_000);
}

// ── The end screen ───────────────────────────────────────────────────────────

export interface EndRow {
  /** When, Requested, Duration, Location, or the payment row's label. */
  readonly label: string;
  readonly value: string;
  /** The amount on the right, or null. */
  readonly amount: string | null;
  /** Which of the app's row icons it takes. */
  readonly icon: 'when' | 'duration' | 'place' | 'online' | 'phone' | 'payment';
  /** On an in-person place: its directions link; null otherwise. */
  readonly directionsHref?: string | null;
  /** A second line under the value: by phone, who calls and the number (service-by-phone D14). */
  readonly line?: string | null;
}

/**
 * The end screen's payment row (rows K5, K5b, K6): a free booking "Payment · Nothing to pay", a
 * free request "Price · Nothing to pay · Free", "Paid via card", a request paid online "Collected
 * after approval", else due at the session.
 */
export function endPaymentRow(p: { amount: number | null | undefined; paid: boolean; request: boolean; payAtSession: boolean }): PriceRow {
  if (!p.amount) return p.request ? { label: 'Price', value: 'Nothing to pay', amount: 'Free' } : { label: 'Payment', value: 'Nothing to pay', amount: null };
  if (p.paid) return { label: 'Paid', value: 'via card', amount: formatCurrency(p.amount) };
  if (p.request && !p.payAtSession) return { label: 'Payment', value: 'Collected after approval', amount: null };
  return { label: 'Due', value: 'at the session', amount: formatCurrency(p.amount) };
}

/**
 * The end screen's rows (web-sign-in-checkout D12): When (Requested on a request), Duration,
 * Location (an in-person one-to-one too; By phone with the call line) and the payment row. A withdrawn request keeps only
 * when and how long, as the app's cancelled view does.
 */
export function endRows(b: BookingDetail, tz: string, request: boolean): EndRow[] {
  const rows: EndRow[] = [
    { label: request ? 'Requested' : 'When', value: formatWhen(b.scheduledAt, tz), amount: null, icon: 'when' },
    { label: 'Duration', value: formatDuration(b.service.duration), amount: null, icon: 'duration' },
  ];
  if (b.status === 'CANCELLED') return rows;
  const loc = b.location;
  if (loc?.isByPhone) {
    rows.push({ label: 'Location', value: 'By phone', amount: null, icon: 'phone', line: phoneCallStep(b.phoneCall)?.line ?? null });
  } else if (loc?.isVirtual) {
    const platform = virtualPlatformLabel(loc.virtualPlatform);
    rows.push({ label: 'Location', value: platform ? `Online on ${platform}` : 'Online', amount: null, icon: 'online' });
  } else if (loc) {
    const place = [loc.name, loc.city, loc.state].filter(Boolean).join(', ');
    if (place) rows.push({ label: 'Location', value: place, amount: null, icon: 'place', directionsHref: buildGoogleMapsUrl(loc) });
  }
  const pay = endPaymentRow({
    amount: b.payment?.amount,
    paid: b.payment?.status === 'PAID',
    request,
    payAtSession: b.paymentRequirement === 'PAY_AT_SESSION',
  });
  rows.push({ label: pay.label, value: pay.value, amount: pay.amount, icon: 'payment' });
  return rows;
}
